// frontend/src/settings/HolidaySettings.js - Supabase対応版
import React, { useEffect, useState } from "react";
import { supabase } from "../supabase";
import { Link } from "react-router-dom";
import Header from "../components/ui/Header";
import { useOrganization } from "../contexts/OrganizationContext";

function HolidaySettings() {
  const { currentOrganization } = useOrganization();
  const [year, setYear] = useState(new Date().getFullYear());
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [orgHolidays, setOrgHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newHolidayDate, setNewHolidayDate] = useState("");
  const [newHolidayName, setNewHolidayName] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [japaneseHolidays, setJapaneseHolidays] = useState([]);
  const [loadingHolidays, setLoadingHolidays] = useState(false);

  // 🆕 追加
  const fetchJapaneseHolidaysFromAPI = async () => {
    if (japaneseHolidays.length > 0) return; // すでに取得済み
    
    console.log("🔄 日本の祝日APIを呼び出し中..."); // ← デバッグログ追加
    
    setLoadingHolidays(true);
    try {
      const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/fetch-japanese-holidays`;
      console.log("📡 API URL:", url); // ← デバッグログ追加
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
        }
      });
      
      console.log("📥 API Response Status:", response.status); // ← デバッグログ追加
      
      const data = await response.json();
      console.log("✅ 祝日データ取得:", data); // ← デバッグログ追加
      
      setJapaneseHolidays(data.holidays || []);
    } catch (error) {
      console.error('祝日取得エラー:', error);
      alert('日本の祝日データの取得に失敗しました');
    } finally {
      setLoadingHolidays(false);
    }
  };  

  useEffect(() => {
    if (currentOrganization?.id) {
      loadHolidays();
      fetchJapaneseHolidaysFromAPI();
    }
  }, [year, currentOrganization]);

  const loadHolidays = async () => {
    console.log("🔄 祝日データ読み込み開始");
    console.log("  - 組織ID:", currentOrganization?.id);
    console.log("  - 年:", year);
    
    // 🔥 組織IDがない場合は処理をスキップ
    if (!currentOrganization?.id) {
      console.log("⚠️ 組織IDなし - スキップ");
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const organizationId = currentOrganization.id;
  
      // 公的祝日を取得
      const { data: publicData, error: publicError } = await supabase
        .from('holidays')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('year', year)
        .eq('is_public', true)
        .order('date');
  
      if (publicError) throw publicError;
      
      console.log("✅ 公的祝日取得:", publicData?.length, "件"); // ← 追加
  
      // 組織独自の祝日を取得
      const { data: orgData, error: orgError } = await supabase
        .from('holidays')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('year', year)
        .eq('is_public', false)
        .order('date');
  
      if (orgError) throw orgError;
      
      console.log("✅ 組織独自祝日取得:", orgData?.length, "件"); // ← 追加
  
      setPublicHolidays(publicData || []);
      setOrgHolidays(orgData || []);
  
    } catch (error) {
      console.error("❌ 祝日データ読み込みエラー:", error);
      alert("祝日データの読み込みに失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const importJapaneseHolidays = async () => {
    const yearHolidays = japaneseHolidays.filter(h => h.year === year);
    
    if (yearHolidays.length === 0) {
      alert("この年の日本の祝日データがありません");
      return;
    }
  
    if (!window.confirm(`${year}年の日本の祝日をインポートしますか？\n既存の公的祝日は上書きされます。`)) {
      return;
    }
  
    try {
      const organizationId = currentOrganization.id;
  
      // 既存の公的祝日を削除
      const { error: deleteError } = await supabase
        .from('holidays')
        .delete()
        .eq('organization_id', organizationId)
        .eq('year', year)
        .eq('is_public', true);
  
      if (deleteError) throw deleteError;
  
      // 新しい祝日を追加
      const holidaysToInsert = yearHolidays.map(h => ({
        organization_id: organizationId,
        year: year,
        date: h.date,
        name: h.name,
        is_public: true
      }));
  
      const { error: insertError } = await supabase
        .from('holidays')
        .insert(holidaysToInsert);
  
      if (insertError) throw insertError;
  
      await loadHolidays();
      showMessage("日本の祝日をインポートしました", "success");
    } catch (error) {
      console.error("インポートエラー:", error);
      alert("インポートに失敗しました: " + error.message);
    }
  };

  const addOrgHoliday = async () => {
    if (!newHolidayDate) {
      alert("日付を入力してください");
      return;
    }
  
    if (!newHolidayName.trim()) {
      alert("祝日名を入力してください");
      return;
    }
  
    if (orgHolidays.some(h => h.date === newHolidayDate)) {
      alert("この日付は既に組織独自の祝日として登録されています");
      return;
    }
  
    try {
      const organizationId = currentOrganization.id; 

      const { error } = await supabase
        .from('holidays')
        .insert({
          organization_id: organizationId,
          year: year,
          date: newHolidayDate,
          name: newHolidayName,
          is_public: false
        });

      if (error) throw error;

      setNewHolidayDate("");
      setNewHolidayName("");
      setShowAddForm(false);
      
      await loadHolidays();
      showMessage("組織独自の祝日を追加しました", "success");
    } catch (error) {
      console.error("追加エラー:", error);
      alert("追加に失敗しました: " + error.message);
    }
  };

  const removeOrgHoliday = async (holiday) => {
    if (!window.confirm(`${holiday.date} (${holiday.name}) を削除しますか？`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('holidays')
        .delete()
        .eq('id', holiday.id);

      if (error) throw error;

      await loadHolidays();
      showMessage("組織独自の祝日を削除しました", "success");
    } catch (error) {
      console.error("削除エラー:", error);
      alert("削除に失敗しました: " + error.message);
    }
  };

  const removePublicHoliday = async (holiday) => {
    if (!window.confirm(`${holiday.date} (${holiday.name}) を公的祝日から削除しますか？`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('holidays')
        .delete()
        .eq('id', holiday.id);

      if (error) throw error;

      await loadHolidays();
      showMessage("公的祝日を削除しました", "success");
    } catch (error) {
      console.error("削除エラー:", error);
      alert("削除に失敗しました: " + error.message);
    }
  };

  const showMessage = (text, type = "success") => {
    const message = document.createElement("div");
    message.textContent = text;
    message.style.position = "fixed";
    message.style.bottom = "20px";
    message.style.right = "20px";
    message.style.backgroundColor = type === "success" ? "#10b981" : "#ef4444";
    message.style.color = "white";
    message.style.padding = "1rem 1.5rem";
    message.style.borderRadius = "8px";
    message.style.boxShadow = "0 4px 6px rgba(0,0,0,0.1)";
    message.style.zIndex = "10000";
    document.body.appendChild(message);
    setTimeout(() => message.remove(), 3000);
  };

  const styles = {
    container: {
      minHeight: "100vh",
      backgroundColor: "#f3f4f6",
      paddingTop: "80px",
      paddingBottom: "2rem"
    },
    backLink: {
      display: "inline-block",
      marginLeft: "2rem",
      marginBottom: "1rem",
      color: "#3b82f6",
      textDecoration: "none",
      fontSize: "0.95rem"
    },
    card: {
      maxWidth: "1200px",
      margin: "0 auto",
      backgroundColor: "white",
      borderRadius: "12px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      padding: "2rem"
    },
    header: {
      borderBottom: "2px solid #e5e7eb",
      paddingBottom: "1rem",
      marginBottom: "2rem",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center"
    },
    yearSelector: {
      display: "flex",
      alignItems: "center",
      gap: "1rem"
    },
    yearButton: {
      padding: "0.5rem 1rem",
      backgroundColor: "#3b82f6",
      color: "white",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      fontSize: "0.9rem"
    },
    yearDisplay: {
      fontSize: "1.5rem",
      fontWeight: "bold",
      minWidth: "120px",
      textAlign: "center"
    },
    section: {
      marginBottom: "2rem"
    },
    sectionTitle: {
      fontSize: "1.3rem",
      fontWeight: "bold",
      marginBottom: "1rem",
      color: "#1f2937",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center"
    },
    importButton: {
      padding: "0.5rem 1rem",
      backgroundColor: "#10b981",
      color: "white",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      fontSize: "0.9rem"
    },
    addButton: {
      padding: "0.5rem 1rem",
      backgroundColor: "#8b5cf6",
      color: "white",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      fontSize: "0.9rem"
    },
    holidayList: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
      gap: "1rem"
    },
    holidayItem: {
      padding: "1rem",
      backgroundColor: "#f9fafb",
      borderRadius: "8px",
      border: "1px solid #e5e7eb",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center"
    },
    holidayInfo: {
      flex: 1
    },
    holidayDate: {
      fontWeight: "bold",
      color: "#1f2937",
      marginBottom: "0.25rem"
    },
    holidayName: {
      fontSize: "0.9rem",
      color: "#6b7280"
    },
    dayOfWeek: {
      fontSize: "0.85rem",
      color: "#9ca3af",
      marginTop: "0.25rem"
    },
    deleteButton: {
      padding: "0.4rem 0.8rem",
      backgroundColor: "#ef4444",
      color: "white",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: "0.85rem"
    },
    addForm: {
      padding: "1.5rem",
      backgroundColor: "#f0f9ff",
      borderRadius: "8px",
      marginBottom: "1rem"
    },
    formGroup: {
      marginBottom: "1rem"
    },
    label: {
      display: "block",
      marginBottom: "0.5rem",
      fontWeight: "500",
      color: "#374151"
    },
    input: {
      width: "100%",
      padding: "0.75rem",
      border: "1px solid #d1d5db",
      borderRadius: "6px",
      fontSize: "1rem",
      boxSizing: "border-box"
    },
    formButtons: {
      display: "flex",
      gap: "1rem",
      marginTop: "1rem"
    },
    saveButton: {
      padding: "0.75rem 1.5rem",
      backgroundColor: "#10b981",
      color: "white",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      fontSize: "1rem"
    },
    cancelButton: {
      padding: "0.75rem 1.5rem",
      backgroundColor: "#6b7280",
      color: "white",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      fontSize: "1rem"
    },
    infoBox: {
      backgroundColor: "#e7f3ff",
      padding: "1rem",
      borderRadius: "8px",
      marginBottom: "1.5rem",
      border: "1px solid #b8daff"
    },
    infoText: {
      color: "#004085",
      margin: 0,
      fontSize: "0.95rem",
      lineHeight: "1.6"
    },
    emptyMessage: {
      padding: "2rem",
      textAlign: "center",
      color: "#9ca3af",
      fontSize: "0.95rem"
    }
  };

  const getDayOfWeek = (dateStr) => {
    const dayNames = ["日", "月", "火", "水", "木", "金", "土"];
    const date = new Date(dateStr);
    return dayNames[date.getDay()];
  };

  if (!currentOrganization?.id) {
    return (
      <div style={styles.container}>
        <Header />
        <Link to="/settings" style={styles.backLink}>← 設定メニューへ戻る</Link>
        <div style={styles.card}>
          <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>
            組織を選択してください
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <Header />
      
      <Link to="/settings" style={styles.backLink}>← 設定メニューへ戻る</Link>
      
      <div style={styles.card}>
        <div style={styles.header}>
          <h2 style={{ margin: 0 }}>祝祭日設定</h2>
          <div style={styles.yearSelector}>
            <button 
              style={styles.yearButton}
              onClick={() => setYear(year - 1)}
            >
              ◀ 前年
            </button>
            <div style={styles.yearDisplay}>{year}年</div>
            <button 
              style={styles.yearButton}
              onClick={() => setYear(year + 1)}
            >
              翌年 ▶
            </button>
          </div>
        </div>

        <div style={styles.infoBox}>
          <p style={styles.infoText}>
            シフト自動作成時に休日として扱われる日を設定します。<br/>
            公的祝日（日本の祝日）と組織独自の祝日を別々に管理できます。
          </p>
        </div>

        {loading ? (
          <div style={styles.emptyMessage}>読み込み中...</div>
        ) : (
          <>
            {/* 公的祝日セクション */}
            <div style={styles.section}>
              <div style={styles.sectionTitle}>
                <span>📅 公的祝日</span>
                <button 
                style={styles.importButton}
                onClick={importJapaneseHolidays}
                disabled={loadingHolidays || japaneseHolidays.filter(h => h.year === year).length === 0}
              >
                {loadingHolidays ? "読み込み中..." : "日本の祝日をインポート"}
              </button>
              </div>
              
              {publicHolidays.length === 0 ? (
                <div style={styles.emptyMessage}>
                  公的祝日が設定されていません。<br/>
                  「日本の祝日をインポート」ボタンをクリックして追加できます。
                </div>
              ) : (
                <div style={styles.holidayList}>
                  {publicHolidays.map(holiday => (
                    <div key={holiday.id} style={styles.holidayItem}>
                      <div style={styles.holidayInfo}>
                        <div style={styles.holidayDate}>{holiday.date}</div>
                        <div style={styles.holidayName}>{holiday.name}</div>
                        <div style={styles.dayOfWeek}>({getDayOfWeek(holiday.date)})</div>
                      </div>
                      <button
                        style={styles.deleteButton}
                        onClick={() => removePublicHoliday(holiday)}
                      >
                        削除
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 組織独自の祝日セクション */}
            <div style={styles.section}>
              <div style={styles.sectionTitle}>
                <span>🏢 組織独自の祝日</span>
                <button 
                  style={styles.addButton}
                  onClick={() => setShowAddForm(!showAddForm)}
                >
                  {showAddForm ? "キャンセル" : "+ 追加"}
                </button>
              </div>

              {showAddForm && (
                <div style={styles.addForm}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>日付</label>
                    <input
                      type="date"
                      style={styles.input}
                      value={newHolidayDate}
                      onChange={(e) => setNewHolidayDate(e.target.value)}
                      min={`${year}-01-01`}
                      max={`${year}-12-31`}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>祝日名</label>
                    <input
                      type="text"
                      style={styles.input}
                      value={newHolidayName}
                      onChange={(e) => setNewHolidayName(e.target.value)}
                      placeholder="例: 創立記念日"
                    />
                  </div>
                  <div style={styles.formButtons}>
                    <button style={styles.saveButton} onClick={addOrgHoliday}>
                      追加
                    </button>
                    <button 
                      style={styles.cancelButton} 
                      onClick={() => {
                        setShowAddForm(false);
                        setNewHolidayDate("");
                        setNewHolidayName("");
                      }}
                    >
                      キャンセル
                    </button>
                  </div>
                </div>
              )}
              
              {orgHolidays.length === 0 ? (
                <div style={styles.emptyMessage}>
                  組織独自の祝日が設定されていません。<br/>
                  「+ 追加」ボタンをクリックして追加できます。
                </div>
              ) : (
                <div style={styles.holidayList}>
                  {orgHolidays.map(holiday => (
                    <div key={holiday.id} style={styles.holidayItem}>
                      <div style={styles.holidayInfo}>
                        <div style={styles.holidayDate}>{holiday.date}</div>
                        <div style={styles.holidayName}>{holiday.name}</div>
                        <div style={styles.dayOfWeek}>({getDayOfWeek(holiday.date)})</div>
                      </div>
                      <button
                        style={styles.deleteButton}
                        onClick={() => removeOrgHoliday(holiday)}
                      >
                        削除
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default HolidaySettings;